import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY = '#263078';
const PRIMARY_LT = '#e8eaf6';

const fmt2Date = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtAmt = (n) =>
  `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtRatio = (n) => (parseFloat(n) || 0).toFixed(2);

const iconBtn = {
  color: 'rgba(255,255,255,0.85)',
  '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' },
  borderRadius: '6px', p: '4px',
};

const health = (label, val) => {
  const v = parseFloat(val) || 0;
  if (label === 'Current Ratio') {
    if (v >= 2)  return { bg: '#d1fae5', color: '#059669', text: 'Healthy' };
    if (v >= 1)  return { bg: '#fef3c7', color: '#b45309', text: 'Moderate' };
    return             { bg: '#fecaca', color: '#dc2626', text: 'Low' };
  }
  if (v >= 20) return { bg: '#d1fae5', color: '#059669', text: 'Good' };
  if (v >= 10) return { bg: '#fef3c7', color: '#b45309', text: 'Average' };
  return             { bg: '#fecaca', color: '#dc2626', text: 'Low' };
};

// Compact row: label | value | badge?
const Row = ({ label, value, isRatio, suffix }) => {
  const h = isRatio ? health(label, value) : null;
  return (
    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
      <td style={{ padding: '5px 10px', fontSize: 11, color: '#64748b', width: '52%' }}>{label}</td>
      <td style={{ padding: '5px 10px', fontSize: 12, fontWeight: 700, textAlign: 'right',
        color: isRatio ? h.color : (parseFloat(value) < 0 ? '#dc2626' : '#1565c0') }}>
        {isRatio ? `${fmtRatio(value)}${suffix}` : fmtAmt(value)}
      </td>
      <td style={{ padding: '5px 8px', width: 72 }}>
        {isRatio && (
          <span style={{ padding: '1px 7px', borderRadius: 8, fontSize: 10, fontWeight: 600,
            background: h.bg, color: h.color, display: 'inline-block', whiteSpace: 'nowrap' }}>
            {h.text}
          </span>
        )}
      </td>
    </tr>
  );
};

// Compact section card
const Card = ({ title, accent, children }) => (
  <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden', flex: 1, minWidth: 220 }}>
    <div style={{ background: accent, padding: '5px 10px' }}>
      <span style={{ color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: 0.3 }}>{title}</span>
    </div>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const RatioAnalysisReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [fetched, setFetched] = useState(false);

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    if (!companyId || !branchId || !token) { setError('Authentication data missing'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetchData('MISReportApi', `/RatioAnalysis/${fromDate}/${toDate}/${companyId}/${branchId}`, token);
      let d = res.response_data ?? res ?? null;
      if (Array.isArray(d)) d = d.length > 0 ? d[0] : null;
      setData(d && typeof d === 'object' ? d : null);
      setFetched(true);
    } catch (err) {
      console.error('RatioAnalysisReport error:', err);
      setError('Failed to fetch ratio analysis report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'RatioAnalysisReport', 0);
    if (!data) return;
    const rows = [
      ['PROFITABILITY', ''],
      ['Metric', 'Value'],
      ['Sales', fmtAmt(data.Sales)],
      ['Purchase', fmtAmt(data.Purchase)],
      ['Gross Profit', fmtAmt(data.GrossProfit)],
      ['Gross Profit Ratio', `${fmtRatio(data.GrossProfitRatio)}%`],
      [],
      ['INCOME & EXPENSE', ''],
      ['Metric', 'Value'],
      ['Total Income', fmtAmt(data.TotalIncome)],
      ['Total Expense', fmtAmt(data.TotalExpense)],
      ['Net Profit', fmtAmt(data.NetProfit)],
      ['Net Profit Ratio', `${fmtRatio(data.NetProfitRatio)}%`],
      [],
      ['LIQUIDITY', ''],
      ['Metric', 'Value'],
      ['Current Assets', fmtAmt(data.CurrentAssets)],
      ['Current Liabilities', fmtAmt(data.CurrentLiabilities)],
      ['Current Ratio', `${fmtRatio(data.CurrentRatio)}x`],
    ];

    await exportStyledExcel({
      reportTitle: 'RATIO ANALYSIS REPORT',
      sheetName: 'Ratio Analysis',
      fileName: `RatioAnalysis_${fromDate}_${toDate}.xlsx`,
      companyName,
      username,
      fromDate,
      toDate,
      rawRows: rows,
      columnWidths: [28, 18],
      sectionHeaders: ['PROFITABILITY', 'INCOME & EXPENSE', 'LIQUIDITY'],
    });
  };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 8, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading ratio analysis..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '4px 10px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #1a237e 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.goBack()} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>Ratio Analysis</span>
          <div style={{ flex: 1 }} />
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ height: 28, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 7, padding: '0 7px', fontSize: 11, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ height: 28, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 7, padding: '0 7px', fontSize: 11, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}><Refresh sx={{ fontSize: 16 }} /></IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!data} size="small" sx={iconBtn}><GetApp sx={{ fontSize: 16 }} /></IconButton>
          </Tooltip>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'scroll', maxHeight: 'calc(100vh - 110px)', padding: 10 }}>

          {!fetched && !loading && (
            <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>Select date range and click refresh</div>
          )}
          {fetched && !data && !loading && (
            <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>No data found for selected period</div>
          )}

          {data && (
            <>
              {/* KPI Strip */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                {[
                  { label: 'Gross Profit Ratio', value: data.GrossProfitRatio, suffix: '%' },
                  { label: 'Net Profit Ratio',   value: data.NetProfitRatio,   suffix: '%' },
                  { label: 'Current Ratio',       value: data.CurrentRatio,     suffix: 'x' },
                ].map(({ label, value, suffix }) => {
                  const h = health(label, value);
                  return (
                    <div key={label} style={{ flex: 1, minWidth: 140, background: h.bg, borderRadius: 8, padding: '8px 12px', border: `1px solid ${h.color}30`, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 10, color: h.color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
                      <span style={{ fontSize: 20, fontWeight: 800, color: h.color, lineHeight: 1.2 }}>
                        {fmtRatio(value)}<span style={{ fontSize: 12, fontWeight: 500 }}>{suffix}</span>
                      </span>
                      <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 8, fontSize: 10, fontWeight: 600, background: 'rgba(255,255,255,0.7)', color: h.color, width: 'fit-content' }}>
                        {h.text}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* 3 Section Cards side by side */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>

                <Card title="PROFITABILITY" accent="linear-gradient(135deg,#1565c0,#1e3a8a)">
                  <Row label="Sales"             value={data.Sales}           />
                  <Row label="Purchase"          value={data.Purchase}        />
                  <tr style={{ background: PRIMARY_LT, borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '5px 10px', fontSize: 11, fontWeight: 600, color: '#1e293b' }}>Gross Profit</td>
                    <td style={{ padding: '5px 10px', fontSize: 12, fontWeight: 700, textAlign: 'right', color: parseFloat(data.GrossProfit) >= 0 ? '#1565c0' : '#dc2626' }}>{fmtAmt(data.GrossProfit)}</td>
                    <td style={{ width: 72 }} />
                  </tr>
                  <Row label="Gross Profit Ratio" value={data.GrossProfitRatio} isRatio suffix="%" />
                </Card>

                <Card title="INCOME & EXPENSE" accent="linear-gradient(135deg,#6a1b9a,#4a148c)">
                  <Row label="Total Income"    value={data.TotalIncome}  />
                  <Row label="Total Expense"   value={data.TotalExpense} />
                  <tr style={{ background: PRIMARY_LT, borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '5px 10px', fontSize: 11, fontWeight: 600, color: '#1e293b' }}>Net Profit</td>
                    <td style={{ padding: '5px 10px', fontSize: 12, fontWeight: 700, textAlign: 'right', color: parseFloat(data.NetProfit) >= 0 ? '#1565c0' : '#dc2626' }}>{fmtAmt(data.NetProfit)}</td>
                    <td style={{ width: 72 }} />
                  </tr>
                  <Row label="Net Profit Ratio" value={data.NetProfitRatio} isRatio suffix="%" />
                </Card>

                <Card title="LIQUIDITY" accent="linear-gradient(135deg,#00695c,#004d40)">
                  <Row label="Current Assets"      value={data.CurrentAssets}      />
                  <Row label="Current Liabilities" value={data.CurrentLiabilities} />
                  <Row label="Current Ratio"       value={data.CurrentRatio} isRatio suffix="x" />
                </Card>

              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RatioAnalysisReport;
